// ==============================
// FILE: src/components/ImportantDocuments.js
// ==============================
import React, { useState, useEffect, useRef, useMemo } from "react";
import { db, storage } from "../firebase";
import {
  collection, addDoc, query, where, orderBy, onSnapshot,
  updateDoc, deleteDoc, doc, serverTimestamp
} from "firebase/firestore";
import {
  ref, uploadBytesResumable, getDownloadURL, deleteObject
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import {
  openDownloadForAttachment,
  toStoragePath,
  downloadTextFile,
  buildForwardMailto,
} from "../utils/managerActions";
import "../styles/layout.css";

const categories = [
  { label: "Passport", icon: "🛂" },
  { label: "ID", icon: "🆔" },
  { label: "Insurance", icon: "🛡️" },
  { label: "Property", icon: "🏠" },
  { label: "Financial", icon: "💳" },
  { label: "Medical", icon: "💊" },
  { label: "Other", icon: "📁" },
];

function categoryIcon(label) {
  const found = categories.find((x) => x.label === label);
  return found ? found.icon : "📁";
}
function isSingleOnly(cat) {
  return cat === "Passport" || cat === "ID";
}
function isImageURL(url = "") {
  return /(\.(png|jpg|jpeg|gif|webp|bmp|svg))(\?|$)/i.test(url);
}
function pathFromUrl(u) {
  try {
    const enc = u.split("/o/")[1]?.split("?")[0] || "";
    return enc ? decodeURIComponent(enc) : null;
  } catch { return null; }
}

export default function ImportantDocuments({ user }) {
  const { readOnly, ownerUid: managedOwnerUid } =
    useAccess() || { readOnly: false, ownerUid: null };

  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  // header helpers
  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // manager VIEW modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // form state
  const [formData, setFormData] = useState({
    name: "",
    category: "Other",
    files: [],           // File[] picked in this session
    fileUrls: [],        // existing+new URLs
    filePaths: [],       // existing+new Storage paths
    url: "",             // convenience: first URL
    dateAdded: null,
    message: "",
  });

  // refs
  const filePickerRef = useRef(null);
  const addOneRef = useRef(null);

  // ===== Fetch documents =====
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "documents"),
      where("uid", "==", user.uid),
      orderBy("dateAdded", "desc")
    );
    return onSnapshot(q, snap => {
      setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [user?.uid]);

  // ===== UI helpers =====
  function openForm(docItem = null) {
    if (!docItem && free && docs.length >= 1) {
      setShowUpgrade(true);
      return;
    }
    setError("");

    if (docItem) {
      setEditingId(docItem.id);
      const existingUrls = Array.isArray(docItem.fileUrls)
        ? docItem.fileUrls
        : (docItem.fileUrl ? [docItem.fileUrl] : []);
      const existingPaths = Array.isArray(docItem.filePaths)
        ? docItem.filePaths
        : existingUrls.map(pathFromUrl).filter(Boolean);
      setFormData({
        name: docItem.name || "",
        category: docItem.category || "Other",
        files: [],
        fileUrls: existingUrls,
        filePaths: existingPaths,
        url: existingUrls[0] || "",
        dateAdded: docItem.dateAdded || null,
        message: docItem.message || "",
      });
    } else {
      setEditingId(null);
      setFormData({
        name: "",
        category: "Other",
        files: [],
        fileUrls: [],
        filePaths: [],
        url: "",
        dateAdded: null,
        message: "",
      });
    }
    if (filePickerRef.current) filePickerRef.current.value = "";
    if (addOneRef.current) addOneRef.current.value = "";
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setError("");
    if (filePickerRef.current) filePickerRef.current.value = "";
    if (addOneRef.current) addOneRef.current.value = "";
  }

  // thumbnails for *new* files (object URLs)
  const localThumbs = useMemo(() => {
    return formData.files.map((f) => ({
      url: URL.createObjectURL(f),
      name: f.name
    }));
  }, [formData.files]);

  // revoke created object URLs when unmount/close
  useEffect(() => {
    return () => {
      try { localThumbs.forEach((t) => URL.revokeObjectURL(t.url)); } catch {}
    };
  }, [localThumbs]);

  function handleField(e) {
    const { name, value } = e.target;
    if (name === "category") {
      setFormData((f) => {
        const single = isSingleOnly(value);
        const trimmedFiles = single && f.files.length > 1 ? f.files.slice(0, 1) : f.files;
        const trimmedUrls  = single && f.fileUrls.length > 1 ? f.fileUrls.slice(0, 1) : f.fileUrls;
        const trimmedPaths = single && f.filePaths.length > 1 ? f.filePaths.slice(0, 1) : f.filePaths;
        return {
          ...f,
          category: value,
          files: trimmedFiles,
          fileUrls: trimmedUrls,
          filePaths: trimmedPaths,
          url: trimmedUrls[0] || ""
        };
      });
    } else {
      setFormData((f) => ({ ...f, [name]: value }));
    }
  }

  function handleAddFiles(e, forceSingle = false) {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    setFormData((f) => {
      const single = isSingleOnly(f.category) || forceSingle;
      let nextFiles = single ? [picked[0]] : [...f.files, ...picked];
      return { ...f, files: nextFiles };
    });

    e.target.value = "";
  }

  function removeNewFile(idx) {
    setFormData((f) => {
      const copy = [...f.files];
      copy.splice(idx, 1);
      return { ...f, files: copy };
    });
  }

  function removeExistingUrl(idx) {
    setFormData((f) => {
      const urls = [...f.fileUrls];
      const paths = [...(f.filePaths || [])];
      urls.splice(idx, 1);
      if (paths.length) paths.splice(idx, 1);
      return {
        ...f,
        fileUrls: urls,
        filePaths: paths,
        url: urls[0] || ""
      };
    });
  }

  // ===== Save / Delete =====
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const { name, category, files, fileUrls, filePaths, url, dateAdded, message } = formData;

    try {
      let uploadedUrls = [...fileUrls];
      let uploadedPaths = [...(filePaths || [])];

      if (files && files.length) {
        const queue = isSingleOnly(category) ? [files[0]] : files;

        for (const file of queue) {
          const fname = `${user.uid}_${Date.now()}_${file.name.replace(/[^\w.-]+/g, "_")}`;
          const sref = ref(storage, `documents/${user.uid}/${fname}`);
          const task = uploadBytesResumable(sref, file);
          await new Promise((res, rej) => task.on("state_changed", null, rej, res));
          const u = await getDownloadURL(task.snapshot.ref);
          uploadedUrls.push(u);
          uploadedPaths.push(`documents/${user.uid}/${fname}`);
          if (isSingleOnly(category)) break;
        }

        if (isSingleOnly(category)) {
          uploadedUrls = uploadedUrls.slice(-1);
          uploadedPaths = uploadedPaths.slice(-1);
        }
      }

      const firstUrl = uploadedUrls[0] || url || "";
      const firstPath = uploadedPaths[0] || null;

      const payload = {
        uid: user.uid,
        name,
        category,
        message: message || "",
        fileUrl: firstUrl,
        fileUrls: uploadedUrls,
        filePath: firstPath,
        filePaths: uploadedPaths,
        dateAdded: editingId ? (dateAdded || null) : serverTimestamp(),
        lastUpdated: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "documents", editingId), payload);
      } else {
        await addDoc(collection(db, "documents"), payload);
      }
      closeForm();
    } catch (err) {
      setError(err.message || "Save failed");
    }
  }

  async function handleDelete() {
    if (!editingId) return;
    if (!window.confirm("Delete this document?")) return;
    try {
      const item = docs.find((d) => d.id === editingId);
      const paths = Array.isArray(item?.filePaths) && item.filePaths.length
        ? item.filePaths
        : (Array.isArray(item?.fileUrls)
            ? item.fileUrls.map(pathFromUrl).filter(Boolean)
            : (item?.fileUrl ? [pathFromUrl(item.fileUrl)].filter(Boolean) : [])
          );

      for (const p of paths) {
        try { await deleteObject(ref(storage, p)); } catch {}
      }
      await deleteDoc(doc(db, "documents", editingId));
      closeForm();
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  }

  // ===== Manager VIEW modal =====
  function openView(item) {
    setViewItem(item);
    setViewOpen(true);
  }
  function closeView() {
    setViewOpen(false);
    setViewItem(null);
  }

  // Helper: build list of file paths (prefer explicit filePaths)
  function pathsFor(item) {
    if (!item) return [];
    if (Array.isArray(item.filePaths) && item.filePaths.length) return item.filePaths.slice();
    if (Array.isArray(item.fileUrls) && item.fileUrls.length) {
      return item.fileUrls.map((u) => toStoragePath(u)).filter(Boolean);
    }
    if (item.filePath) return [item.filePath];
    if (item.fileUrl) return [toStoragePath(item.fileUrl)].filter(Boolean);
    return [];
  }

  const actionsDesktop = (
    <>
      <button className="btn id-help read" onClick={() => setShowDrawer(true)} data-allow-readonly="true">
        <FiEye className="id-help-ico" aria-hidden />
        Read how it works
      </button>
      <button className="btn id-help see" onClick={() => setShowVideo(true)} data-allow-readonly="true">
        <FaYoutube className="id-help-ico" aria-hidden />
        See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="id-help-row">
      <button className="btn id-help read" onClick={() => setShowDrawer(true)} data-allow-readonly="true">
        <FiEye className="id-help-ico" aria-hidden />
        Read how it works
      </button>
      <button className="btn id-help see" onClick={() => setShowVideo(true)} data-allow-readonly="true">
        <FaYoutube className="id-help-ico" aria-hidden />
        See how it works
      </button>
    </div>
  );

  return (
    <>
      <PageScaffold
        title="Important Documents"
        onAdd={!readOnly ? () => openForm(null) : undefined}
        heroSrc="/lastwish-account-documents.jpg"
        heroAlt="Important Documents"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}

      >
        {/* 2-column grid on desktop, stack on mobile */}
        <div className="id-grid">
          {docs.length === 0 ? (
            <div className="empty-state">No documents uploaded yet.</div>
          ) : (
            docs.map((d) => {
              const urls = Array.isArray(d.fileUrls) ? d.fileUrls : (d.fileUrl ? [d.fileUrl] : []);
              const firstImage = urls.find(isImageURL) || null;
              const thumb = firstImage ? (
                <img src={firstImage} alt="" className="tile-thumb" />
              ) : (
                <div className="tile-thumb" style={{display:"grid",placeItems:"center",background:"#f3f3f3",fontSize:"1.2rem"}}>
                  {categoryIcon(d.category)}
                </div>
              );
              const onPrimary = readOnly ? () => openView(d) : () => openForm(d);
              const label = readOnly ? "View" : "Edit";
              return (
                <div key={d.id} className="card id-card">
                  <div className="tile" style={{ flex: 1 }}>
                    <div className="tile-left">{thumb}</div>
                    <div className="tile-main">
                      <div className="tile-title">{d.name}</div>
                      <div className="tile-sub">
                        {d.category}
                        {Array.isArray(urls) && urls.length > 1 ? ` · ${urls.length} files` : ""}
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-main"
                    onClick={onPrimary}
                    {...(readOnly ? { "data-allow-readonly": "1" } : {})}
                  >
                    {label}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PageScaffold>

      {/* ===== Document Form Modal (Owner only) ===== */}
      {showForm && (
        <div className="modal-bg" onClick={closeForm}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Document" : "Upload New Document"}</h3>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <label className="label">Document Name</label>
                <input
                  name="name"
                  className="input"
                  placeholder="e.g. Passport"
                  value={formData.name}
                  onChange={handleField}
                  required
                />

                <label className="label">Message (optional)</label>
                <textarea
                  name="message"
                  className="input"
                  rows={3}
                  value={formData.message}
                  onChange={handleField}
                  placeholder="Add a note about this document…"
                />

                <label className="label">Category</label>
                <select
                  name="category"
                  className="input"
                  value={formData.category}
                  onChange={handleField}
                >
                  {categories.map((cat) => (
                    <option key={cat.label} value={cat.label}>{cat.icon} {cat.label}</option>
                  ))}
                </select>

                {/* Existing files */}
                {(formData.fileUrls?.length > 0) && (
                  <div className="id-thumbs" style={{ marginTop: 6 }}>
                    {formData.fileUrls.map((u, i) => (
                      <div key={`ex-${i}`} className="id-thumb">
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open file"
                          style={{ display: "block", width: "100%", height: "100%" }}
                        >
                          {isImageURL(u) ? (
                            <img src={u} alt="" />
                          ) : (
                            <div className="id-filechip">📄</div>
                          )}
                        </a>
                        <button
                          type="button"
                          className="id-thumb-x"
                          aria-label="Remove file"
                          onClick={() => removeExistingUrl(i)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New files (local previews) */}
                {localThumbs.length > 0 && (
                  <div className="id-thumbs">
                    {localThumbs.map((t, i) => (
                      <div key={`new-${i}`} className="id-thumb">
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={t.name}
                          style={{ display: "block", width: "100%", height: "100%" }}
                        >
                          <img src={t.url} alt="" />
                        </a>
                        <button
                          type="button"
                          className="id-thumb-x"
                          aria-label="Remove file"
                          onClick={() => removeNewFile(i)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* File pickers */}
                {isSingleOnly(formData.category) ? (
                  <div className="id-upload-row">
                    <input
                      ref={filePickerRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={(e) => handleAddFiles(e, true)}
                      style={{ display: "none" }}
                    />
                    {!(formData.files.length || formData.fileUrls.length) ? (
                      <button
                        type="button"
                        className="btn id-upload-cta"
                        onClick={() => filePickerRef.current?.click()}
                      >
                        Choose file
                      </button>
                    ) : (
                      <div className="id-upload-hint">Remove the current file to replace it.</div>
                    )}
                  </div>
                ) : (
                  <div className="id-upload-row">
                    <input
                      ref={addOneRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleAddFiles}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      className="btn id-upload-cta"
                      onClick={() => addOneRef.current?.click()}
                      title="Add another file"
                    >
                      + Add file
                    </button>
                    <div className="id-upload-hint">Attach as many files as you need.</div>
                  </div>
                )}

                {error && <div className="empty-state" style={{ color: "#9b1c1c" }}>{error}</div>}

                <div className="form-actions id-form-actions">
                  <button type="submit" className="btn btn-main">
                    {editingId ? "Update" : "Save"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDelete}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={closeForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== VIEW modal (Managers) ===== */}
      {readOnly && viewOpen && viewItem && (
        <div className="modal-bg" onClick={closeView}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 780 }}>
            <h3>Document</h3>
            <div className="modal-body">
              <div className="card" style={{ padding: 12 }}>
                <div className="grid2" style={{ gap: 10 }}>
                  <div>
                    <label className="label">Name</label>
                    <div className="card" style={{ padding: 10 }}>{viewItem.name || "-"}</div>
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <div className="card" style={{ padding: 10 }}>{viewItem.category || "-"}</div>
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <label className="label">Message</label>
                  <div className="card" style={{ padding: 10, whiteSpace: "pre-wrap" }}>{viewItem.message || "-"}</div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <label className="label">Files</label>
                  <div className="id-thumbs">
                    {(Array.isArray(viewItem.fileUrls) ? viewItem.fileUrls : (viewItem.fileUrl ? [viewItem.fileUrl] : [])).map((u, i) => (
                      <div key={`v-${i}`} className="id-thumb">
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open file"
                          style={{ display: "block", width: "100%", height: "100%" }}
                          data-allow-readonly="true"
                        >
                          {isImageURL(u) ? (
                            <img src={u} alt="" />
                          ) : (
                            <div className="id-filechip">📄</div>
                          )}
                        </a>
                      </div>
                    ))}
                    {(!viewItem.fileUrl && !(viewItem.fileUrls || []).length) && (
                      <div className="empty-state">No files attached.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions" style={{ marginTop: 12 }}>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const owner = managedOwnerUid || user?.uid;
                    if (!owner) return;

                    const filePaths = pathsFor(viewItem);
                    for (let i = 0; i < filePaths.length; i++) {
                      // eslint-disable-next-line no-await-in-loop
                      await openDownloadForAttachment({
                        ownerUid: owner,
                        pathOrUrl: filePaths[i],
                        suggestedName:
                          (viewItem.name?.replace(/[^\w.-]+/g, "_") || "document") +
                          (filePaths.length > 1 ? `_${i + 1}` : ""),
                      });
                    }
                    const fields = [
                      ["Name", viewItem.name],
                      ["Category", viewItem.category],
                      ["Message", viewItem.message],
                    ];
                    const txt = fields.map(([k, v]) => `${k}: ${v ? String(v) : "-"}`).join("\n\n");
                    const fname = (viewItem.name?.replace(/[^\w.-]+/g, "_") || "document") + ".txt";
                    downloadTextFile(fname, txt);
                  }}
                  data-allow-readonly="true"
                >
                  Download
                </button>

                <button className="btn btn-main" type="button" onClick={() => window.print()} data-allow-readonly="true">
                  Print
                </button>

                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const owner = managedOwnerUid || user?.uid;
                    const paths = pathsFor(viewItem);
                    const mailto = await buildForwardMailto({
                      ownerUid: owner,
                      pathOrUrl: paths[0] || null,
                      subject: `Document: ${viewItem.name || ""}`,
                      fields: {
                        Name: viewItem.name || "-",
                        Category: viewItem.category || "-",
                        Message: viewItem.message || "-",
                        Files: (Array.isArray(viewItem.fileUrls) ? viewItem.fileUrls.length : (viewItem.fileUrl ? 1 : 0)) || 0
                      }
                    });
                    window.location.href = mailto;
                  }}
                  data-allow-readonly="true"
                >
                  Forward
                </button>

                <button className="btn btn-cancel" type="button" onClick={closeView} data-allow-readonly="true">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Video modal (new popup player) ===== */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/FmPWmLWjSyY?si=HTBIIi0ChwMQKfdP"
        title="How Important Documents works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* ===== Right-hand drawer for “Read how it works” ===== */}
      {showDrawer && (
        <div className="id-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="id-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="id-drawer-head">
              <div className="id-drawer-title">About Important Documents</div>
              <div className="id-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly="true">
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly="true">
                  Close
                </button>
              </div>
            </div>
            <div className="id-drawer-body">
              <p>
                Store your vital documents—like IDs, insurance, and legal papers—securely in one place.
                You control access. Upload images or PDFs and keep everything organised by category.
              </p>
              <ol>
                <li>Click the + button to add a new document.</li>
                <li>For Passport/ID you can attach a single file; other categories support multiple files via “+ Add file”.</li>
                <li>Use “Edit” to update or download later; “Delete” to remove.</li>
              </ol>
              <p>Tip: Use clear names (e.g. “Passport – John”) so you can quickly find what you need.</p>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        :root { --orange:#f15822; --blue:#8cade1; --aubergine:#2a0516; }

        /* helper buttons */
        .id-help-row { display: flex; gap: 10px; flex-wrap: wrap; }
        .id-help {
          border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px;
          font-weight: 700; color: #fff; border: none;
        }
        .id-help.read { background: #650202; color: #fff; }
        .id-help.see  { background: #003353; color: #fff; }
        .id-help-ico { font-size: 1.05rem; }

        /* Two-column grid */
        .id-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        @media (max-width: 700px) { .id-grid { grid-template-columns: 1fr; gap: 12px; } }

        .id-card { display:flex; align-items:center; gap:12px; padding:12px; }
        .tile-left { width: 42px; }
        .tile-thumb { width:42px; height:42px; border-radius:8px; object-fit:cover; display:block; background:#f6f6f6; }

        /* Thumbs inside modals/forms */
        .id-thumbs { display:flex; gap:8px; flex-wrap:wrap; margin: 8px 0 10px; }
        .id-thumb {
          position: relative; display:inline-block; width:58px; height:58px;
          border-radius:8px; overflow:hidden; background:#f6f6f6; border:1px solid #e8e8e8;
        }
        .id-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
        .id-filechip { width:100%; height:100%; display:grid; place-items:center; font-size:18px; color:#2a0516; }
        .id-thumb-x {
          position:absolute; top:-8px; right:-8px; width:22px; height:22px; border-radius:50%;
          border:none; background:#980000; color:#fff; font-weight:700; cursor:pointer; box-shadow:0 2px 6px rgba(0,0,0,.2);
        }

        /* Upload controls */
        .id-upload-row { display:flex; align-items:center; gap:10px; margin:6px 0 2px; flex-wrap:wrap; }
        .id-upload-cta {
          background:#f0f3fa; color:#003353; border:1px solid #dce3f5; border-radius:8px;
          font-weight:700; padding:8px 12px;
        }
        .id-upload-hint { font-size:12px; color:#657899; }

        /* Form actions (Update/Delete/Cancel) */
        .id-form-actions {
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
        }
        .id-form-actions .btn { min-width: 120px; }
        @media (max-width: 640px) {
          .id-form-actions {
            flex-direction: column;
            align-items: stretch;
          }
          .id-form-actions .btn { width: 100%; }
        }

        /* Video */
        .id-video { position: relative; width: 100%; padding-top: 56.25%; border-radius: 10px; overflow: hidden; background: #000; margin-bottom: 12px; }
        .id-video iframe { position: absolute; inset: 0; width: 100%; height: 100%; }

        /* Drawer */
        .id-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .id-drawer {
          position: fixed; top: var(--topbar-h, 56px); right: 0;
          height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw);
          background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15);
          border-top-left-radius: 12px; border-bottom-left-radius: 12px;
          display: flex; flex-direction: column; overflow: hidden; animation: id-slide-in .22s ease-out both;
        }
        @keyframes id-slide-in { from { transform: translateX(20px); opacity:0; } to { transform: translateX(0); opacity:1; } }
        .id-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .id-drawer-title { font-weight:700; color:#2a0516; }
        .id-drawer-tools { display:flex; gap:8px; }
        .id-drawer-body { padding:16px; overflow:auto; }

        /* Modal normalization */
        .modal-card h3 { background: var(--orange); color: #fff; margin: 0; padding: 16px 20px; font-size: 1.15rem; font-weight: 700; }
        .modal-body { padding: 20px; overflow-y: auto; -webkit-overflow-scrolling: touch; }
        .modal-card .label { font-weight: 600; color: #2a0516; margin: 6px 0; display: block; font-size: .95rem; }
        .modal-card .input, .modal-card select, .modal-card textarea {
          font-size: 1rem; border-radius: 8px; border: 1px solid #d8dce5; padding: 10px 12px; background: #fff;
        }
        .modal-card .form-actions { padding: 12px 20px 18px; border-top: 1px solid #ececec; display: flex; gap: 8px; justify-content: space-between; flex-wrap: wrap; }
        .btn-blue { background: var(--blue); color: #fff; }
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="documents" />
    </>
  );
}
